import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Menu, useMenu, type MenuEntry } from '../../ui/menu';
import { tip } from '../../ui/tooltip';
import type { Binding } from './contracts';
import { generationStatusText, runProgress, timeLeft, type LiveRun } from './GenerationCanvasStatus';

/*
 * Every run on the page as one bar in the zoom pill (Tarek, 14 Oct: "can we make this turn into a
 * progress bar for all generations happening in my canvas"). It replaces the run line that sat
 * under the Generate panel's prompt and spoke for one run. The bar is the runs' average; a press
 * lists each run (its words, how far, how long) — a row shows it on the canvas.
 */

type Reading = { binding: Binding; percent: number; left: string; words: string };

/** The bar's numbers: the runs' average, never going back for a run that was already shown further on. */
export function runsReading(runs: readonly Binding[], live: Record<string, LiveRun>, now: number, floors = new Map<string, number>()) {
  const rows: Reading[] = runs.map(binding => {
    const said = binding.runId ? live[binding.runId] : undefined;
    const progress = runProgress(binding, said, now, floors.get(binding.key) || 0);
    floors.set(binding.key, progress.value);
    const percent = binding.status === 'requesting' ? 0 : progress.percent;
    return { binding, percent, left: timeLeft(binding, progress, now), words: generationStatusText(binding, said).replace(/…$/, '') };
  });
  const percent = rows.length ? Math.round(rows.reduce((sum, row) => sum + row.percent, 0) / rows.length) : 0;
  return { rows, percent };
}

const promptName = (binding: Binding) => {
  const words = binding.request.prompt.trim().replace(/@\[ref:[^\]]*?:([^\]]*)\]/g, '$1').replace(/\s+/g, ' ');
  return words ? (words.length > 44 ? `${words.slice(0, 43).trimEnd()}…` : words) : binding.targetName;
};

export function GenerationRunsBar({ host, runs, live, onShow }: {
  host: HTMLElement | null; runs: readonly Binding[]; live: Record<string, LiveRun>;
  onShow(binding: Binding): void;
}) {
  const [now, setNow] = useState(Date.now);
  const floors = useRef(new Map<string, number>());
  const menu = useMenu();
  const going = runs.length > 0;
  useEffect(() => {
    if (!going) { floors.current.clear(); return; }
    const timer = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(timer);
  }, [going]);
  useEffect(() => { if (!going) menu.close(); }, [going, menu.close]);
  if (!host || !going) return null;
  const { rows, percent } = runsReading(runs, live, now, floors.current);
  const label = runs.length === 1 ? 'Generating' : `${runs.length} generating`;
  const items: MenuEntry[] = rows.map(row => ({
    id: row.binding.key, label: promptName(row.binding), icon: row.binding.request.kind === 'video' ? 'format-video' as const : 'pictures' as const,
    note: [row.words, row.binding.status === 'requesting' ? '' : `${row.percent}%`, row.left].filter(Boolean).join(' · '),
    onSelect: () => onShow(row.binding),
  }));
  return createPortal(<>
    <span className="canvas-nav-divider" aria-hidden />
    <button ref={menu.ref} type="button" className="canvas-nav-button canvas-nav-runs" aria-haspopup="menu" aria-expanded={menu.open}
      aria-label={`${label}, ${percent}%`} {...tip('Generations on this page')} onClick={menu.toggle}>
      <span className="canvas-nav-runs-track" aria-hidden><span style={{ transform: `scaleX(${Math.max(0.02, percent / 100)})` }} /></span>
      <span>{label}</span><span className="canvas-nav-runs-percent tnum">{percent}%</span>
    </button>
    <Menu open={menu.open} anchor={menu.anchor} onClose={menu.close} items={items} placement="above" align="start" label="Generations" width={300} />
  </>, host);
}
